import { Group, Text } from '@byecode/ui'
import type { Field, FieldInputType } from '@lighthouse/core'
import {
    datasourceFieldTypes,
    FieldInputNameMap,
    getRealField,
    getViewColumns,
    ListItem4ByecodeUi,
    relativeSelectShowFieldList,
    SelectDataSource
} from '@lighthouse/shared'
import React, { useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSource, useDataSourceList, useDsListAndFieldOptions } from '@/hooks/useDataSource'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'

import { FieldSelect } from '../../../../FieldSelect'
import { READONLY_FIELDS } from '../constants'
import { getSaveFieldOptions } from '../helps'
import type { DataMode, FieldOptions } from '../types'

interface RelativeSelectConfigProps {
    mode: DataMode
    fieldOptions?: FieldOptions
    field?: Field
    prefix: '' | `columns.${number}.config.`
}

const RelativeSelectConfig: React.FunctionComponent<RelativeSelectConfigProps> = ({ mode, fieldOptions, prefix, field }) => {
    const { watch, setValue, control } = useFormContext()
    const disabledWithVersion = useIsDisabledWithVersion()
    const [inputType, relativePointer = '', fieldPointer = ''] = watch([
        `${prefix}inputType`,
        `${prefix}relativeSelect.relativePointer`,
        `${prefix}fieldPointer`
    ])

    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)

    const { dataSourceOptions: relativeDataSourceOptions, fieldOptions: relativeFieldOptions } = useDsListAndFieldOptions(
        appId,
        envId,
        relativePointer
    )

    const relativeDataSource = useDataSource(appId, envId, relativePointer)

    const [_, disableSaveFiledList] = useMemo(
        () => (inputType ? getSaveFieldOptions(inputType, fieldOptions ?? []) : [[], []]),
        [fieldOptions, inputType]
    )

    const [usedRelativeInputOptions, disableUsedRelativeInputFiledList] = useMemo(
        () => [
            relativeFieldOptions.filter(item => {
                // return fieldSettingMap['text'].includes(item.type) || READONLY_FIELDS.includes(item.type) || item.type === 'number'
                return item.innerType === 'TEXT' || item.innerType === 'NUMBER'
            }),
            disableSaveFiledList.filter(item => !READONLY_FIELDS.includes(item))
        ],
        [relativeFieldOptions, disableSaveFiledList]
    )

    const [showRelativeInputOptions, disableShowRelativeInputFiledList] = useMemo(
        () => [
            relativeFieldOptions.filter(item => {
                if (!relativeDataSource?.schema?.[item.value]) {
                    return false
                }
                const realType = getRealField(relativeDataSource?.schema?.[item.value])?.type
                if (!realType) {
                    return false
                }
                // const realType = relativeDataSource?.schema?.[item.value]?.type
                return relativeSelectShowFieldList.includes(realType)
            }),
            datasourceFieldTypes.filter(item => !relativeSelectShowFieldList.includes(item))
        ],
        [relativeDataSource?.schema, relativeFieldOptions]
    )

    if (field && field?.type === 'role') {
        return null
    }
    return (
        <>
            <Group label="选项内容">
                <ListItem4ByecodeUi alignItems="center" justifyContent="space-between">
                    <Text>选项来源</Text>
                    <Controller
                        name={`${prefix}relativeSelect.relativePointer`}
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <SelectDataSource
                                placeholder="请选择数据表"
                                size="md"
                                value={value}
                                withinPortal
                                disabled={disabledWithVersion}
                                onChange={v => {
                                    onChange(v)
                                    const relativeDataSource = dataSourceList.find(d => d.id === v)
                                    const { schema: relatedSchema, viewOptions: relatedViewOptions } = relativeDataSource ?? { schema: {} }
                                    const { tableProps: relatedTableProps = [] } = relatedViewOptions ?? {}
                                    const currentColumns = getViewColumns({
                                        tableProps: relatedTableProps,
                                        value: [],
                                        schema: relatedSchema
                                    })
                                    setValue(`${prefix}relativeSelect.viewFieldSettings`, currentColumns)
                                }}
                                dataSourceList={dataSourceList}
                            />
                        )}
                    />
                </ListItem4ByecodeUi>
                <ListItem4ByecodeUi alignItems="center" justifyContent="space-between">
                    <Text>选项值</Text>
                    <Controller
                        name={`${prefix}relativeSelect.relativeFieldPointer`}
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <FieldSelect
                                dsId={relativePointer}
                                disabled={usedRelativeInputOptions.length === 0 || disabledWithVersion}
                                options={usedRelativeInputOptions}
                                value={value}
                                onChange={onChange}
                                disableTitle={`${FieldInputNameMap[inputType as FieldInputType]}输入框不支持以下字段：`}
                                disableList={disableUsedRelativeInputFiledList}
                            />
                        )}
                    />
                </ListItem4ByecodeUi>
                <ListItem4ByecodeUi alignItems="center" justifyContent="space-between">
                    <Text>显示为</Text>
                    <Controller
                        name={`${prefix}relativeSelect.relativeShowFieldPointer`}
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <FieldSelect
                                dsId={relativePointer}
                                disabled={showRelativeInputOptions.length === 0 || disabledWithVersion}
                                options={showRelativeInputOptions}
                                value={value}
                                onChange={onChange}
                                disableTitle="不支持以下字段："
                                disableList={disableShowRelativeInputFiledList}
                                disableDesc="只支持选择附件字段"
                            />
                        )}
                    />
                </ListItem4ByecodeUi>
            </Group>
        </>
    )
}

export default RelativeSelectConfig
